<template>
  <section id="how" aria-labelledby="how-heading" class="py-20 px-6 bg-white">
    <div class="max-w-[1300px] mx-auto">
      <!-- How it works -->
      <header class="text-center max-w-3xl mx-auto mb-14">
        <span class="inline-block bg-blue-100 text-[#1E3A5F] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
          How it works
        </span>
        <h2 id="how-heading" class="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
          From sign-up to first results — in days, not months
        </h2>
        <p class="mt-4 text-lg text-gray-600">
          A clear path to running your institution on {{ appName }}, with our team supporting you at every step.
        </p>
      </header>

      <ol class="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20 list-none m-0 p-0">
        <li
          v-for="(step, i) in steps"
          :key="step.title"
          class="relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition"
        >
          <span class="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#1A56DB] text-white text-sm font-bold flex items-center justify-center shadow-md" aria-hidden="true">
            {{ i + 1 }}
          </span>
          <div class="mb-4 text-[#1A56DB]" aria-hidden="true">
            <i :class="['text-2xl', step.icon]"></i>
          </div>
          <h3 class="font-bold text-gray-900 mb-2">
            <span class="sr-only">Step {{ i + 1 }}: </span>{{ step.title }}
          </h3>
          <p class="text-sm text-gray-600 leading-relaxed">{{ step.description }}</p>
        </li>
      </ol>

      <!-- CTA banner -->
      <div class="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1E3A5F] via-[#1A56DB] to-[#1E3A5F] px-8 py-12 lg:p-16 text-center">
        <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,255,255,0.15),transparent_50%)]" aria-hidden="true"></div>
        <div class="relative max-w-3xl mx-auto">
          <h3 class="text-3xl md:text-4xl font-bold text-white leading-tight">
            Ready to run your institution on {{ appName }}?
          </h3>
          <p class="mt-4 text-blue-50 text-lg">
            Book a 30-minute demo with our team. We'll walk through your specific use case
            — whether you run a primary school, a secondary college or a multi-faculty university.
          </p>
          <div class="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <button
              v-if="!isWhiteLabel"
              type="button"
              class="inline-flex items-center justify-center gap-2 bg-white text-[#1E3A5F] hover:bg-gray-100 font-semibold px-6 py-3 rounded-md shadow-md transition border-0 cursor-pointer"
              data-testid="cta-demo-btn"
              @click="landingStore.openDemo(DemoRequestKind.Demo)"
            >
              <i class="pi pi-calendar" aria-hidden="true"></i>
              Request a Demo
            </button>
            <button
              v-if="!isWhiteLabel"
              type="button"
              class="inline-flex items-center justify-center gap-2 bg-transparent border border-white text-white hover:bg-white/10 font-semibold px-6 py-3 rounded-md transition cursor-pointer"
              data-testid="cta-sales-btn"
              @click="landingStore.openDemo(DemoRequestKind.Sales)"
            >
              <i class="pi pi-comments" aria-hidden="true"></i>
              Talk to Sales
            </button>
          </div>
          <p v-if="!isWhiteLabel" class="mt-6 text-sm text-blue-100">
            <i class="pi pi-envelope mr-1" aria-hidden="true"></i>
            Or email us directly at
            <a href="mailto:support@gradeverse.com" class="font-semibold text-white hover:underline">support@gradeverse.com</a>.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useBranding } from '@/composables/useBranding';
import { useLandingStore } from '@/store/landingStore';
import { DemoRequestKind } from '@/types/composition-functions';

const { appName, isWhiteLabel } = useBranding();
const landingStore = useLandingStore();

const steps = [
  {
    icon: 'pi pi-flag',
    title: 'Sign up & set up',
    description: 'Create your school tenant, add your branding, and define your academic calendar and term structure.',
  },
  {
    icon: 'pi pi-upload',
    title: 'Import your data',
    description: 'Bring in students, staff, programmes, courses and curriculum from spreadsheets or your existing system.',
  },
  {
    icon: 'pi pi-cog',
    title: 'Configure modules',
    description: 'Turn on billing, CBT, results and the modules you need. Set roles, permissions and approval flows.',
  },
  {
    icon: 'pi pi-rocket',
    title: 'Go live',
    description: 'Open portals to staff and students. We support every step until you complete your first full term.',
  },
];
</script>
